import gsap from 'gsap'
import './Header.css'
import { splitReveal, splitConceal } from '../../utils/text.js'
import { attachHoverUnderline } from '../../utils/underline.js'
import { STAGGER } from '../../../shared/motion.js'

class AppHeader extends HTMLElement {
    constructor() {
        super()
        this.textEls = []
        this._director = null

        // 動畫與 Split 實例快取
        this._split = null
        this._splitAnimation = null

        // ABOUT ↔ CLOSE 文字切換動畫快取（獨立於整組 Header show/hide）
        this._aboutLabelSplit = null
        this._aboutLabelAnimation = null
    }

    setDirector(director) {
        this._director = director
    }

    connectedCallback() {
        this._renderDOM()
        this._setupDOMReferences()
        this._setupEvents()
        this._setInteractive(false)
    }

    // ─────────────────────────────────────────────────────────────
    // DOM & Initialization Helpers
    // ─────────────────────────────────────────────────────────────

    _renderDOM() {
        this.innerHTML = /* html */ `
            <div class="header-top">
                <button class="header-logo">JIUAN WU</button>
                <button class="header-about">
                    <span class="header-about__label">ABOUT</span>
                </button>
            </div>
        `
    }

    _setupDOMReferences() {
        this.logoBtn = this.querySelector('.header-logo')
        this.aboutBtn = this.querySelector('.header-about')
        this.aboutLabel = this.querySelector('.header-about__label')

        // 收集需要進行 Split 效果的文字元素
        this.textEls = [this.logoBtn, this.aboutLabel]
    }

    _setupEvents() {
        this.logoBtn.addEventListener('click', () => {
            this._director?.requestNavigate('/')
        })

        this.aboutBtn.addEventListener('click', () => {
            if (!this._director) {
                return
            }
            if (this._director.activeView === 'about') {
                this._director.requestNavigate(this._director.router.prev || '/')
                return
            }
            this._director.requestNavigate('/about')
        })

        attachHoverUnderline(this.aboutBtn)
    }

    // ─────────────────────────────────────────────────────────────
    // State & Interactive Handlers
    // ─────────────────────────────────────────────────────────────

    /** 擋掉隱形或動畫轉換狀態下的 Click / Hover 事件 */
    _setInteractive(active) {
        const value = active ? 'auto' : 'none'
        this.logoBtn.style.pointerEvents = value
        this.aboutBtn.style.pointerEvents = value
    }

    _cleanupAnimations() {
        this._splitAnimation?.kill()
        this._split?.revert()
        this._split = null
        this._splitAnimation = null
    }

    // ─────────────────────────────────────────────────────────────
    // Motion Transitions
    // ─────────────────────────────────────────────────────────────

    /** Header 進場：Logo 與 About 按鈕字樣 split 進 */
    show() {
        this._cleanupAnimations()
        this._setInteractive(true)
        this.style.visibility = 'visible'

        const { split, tween } = splitReveal(this.textEls)
        this._split = split
        this._splitAnimation = tween

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** Header 離場：Logo 與 About 按鈕字樣 split 出 */
    hide() {
        this._cleanupAnimations()
        this._setInteractive(false)

        const { split, tween } = splitConceal(this.textEls, { stagger: STAGGER.none })
        this._split = split
        this._splitAnimation = tween

        const tl = gsap.timeline()
        tl.add(tween, 0)
        return tl
    }

    /** 無動畫直接設定 About 按鈕文字，供頁面直接進場（例如深連結 /about）使用 */
    setAboutLabel(text) {
        this.aboutLabel.textContent = text
    }

    _cleanupAboutLabelAnimation() {
        this._aboutLabelAnimation?.kill()
        this._aboutLabelSplit?.revert()
        this._aboutLabelSplit = null
        this._aboutLabelAnimation = null
    }

    /** ABOUT ↔ CLOSE 文字切換：先 split 出場，換字後再 split 進場 */
    _swapAboutLabel(text) {
        this._cleanupAboutLabelAnimation()

        const out = splitConceal([this.aboutLabel], { stagger: STAGGER.none })
        const tl = gsap.timeline()
        if (out.tween) {
            tl.add(out.tween, 0)
        }

        tl.call(() => {
            out.split?.revert()
            this.aboutLabel.textContent = text

            const { split, tween } = splitReveal([this.aboutLabel])
            this._aboutLabelSplit = split
            this._aboutLabelAnimation = tween
        })

        return tl
    }

    /** 進入 about 路由：ABOUT -> CLOSE */
    toClose() {
        return this._swapAboutLabel('CLOSE')
    }

    /** 離開 about 路由：CLOSE -> ABOUT */
    toAbout() {
        return this._swapAboutLabel('ABOUT')
    }

    disconnectedCallback() {
        this._cleanupAnimations()
        this._cleanupAboutLabelAnimation()
    }
}

if (!customElements.get('app-header')) {
    customElements.define('app-header', AppHeader)
}
